import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { usePlatformCopy } from "@/hooks/usePlatformCopy";
import { COPY } from "@/lib/copy";
import { ShieldCheck, Scale, EyeOff, Fingerprint, Users, Landmark } from "lucide-react";

export default function About() {
  const copy = usePlatformCopy();
  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-3xl">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="about-title">
          About Witness Your Vote
        </h1>
        <p className="text-lg text-muted-foreground mt-4 leading-relaxed">{COPY.slogan}</p>
        <p className="text-muted-foreground mt-4 leading-relaxed">{COPY.supporting}</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-10 max-w-4xl">
        {[
          { icon: ShieldCheck, t: "Independent", d: "Not operated by any government, election authority, party or campaign. Participating candidates get public tools — never control of the declaration database or verification process." },
          { icon: EyeOff, t: "Neutral by design", d: "No live totals while voting is open. No leaderboards. Every legitimate registered candidate can participate on identical terms." },
          { icon: Fingerprint, t: "Evidence-grade", d: "Every declaration is sealed with a SHA-256 integrity hash, preserved without silent deletion, and protected by an append-only audit log." },
          { icon: Scale, t: "Lawful and voluntary", d: "Participation is always voluntary. Any apparent discrepancy must be reviewed through proper legal and election processes." },
        ].map((x) => (
          <Card key={x.t}><CardContent className="p-6">
            <x.icon className="h-5 w-5 text-accent mb-3" />
            <h2 className="font-heading font-semibold text-base mb-2">{x.t}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{x.d}</p>
          </CardContent></Card>
        ))}
      </div>

      <div className="max-w-3xl mt-12">
        <h2 className="font-heading font-bold text-2xl text-foreground mb-4">What we are building</h2>
        <div className="space-y-3">
          {[
            ["Now — Phase 1", "Voluntary declarations with verifiable receipts, participating-candidate directory, Ontario Election Centre, newsroom, PITA and accountability programs — in preview."],
            ["Next", "Candidate self-service dashboards, embeddable tools for campaign sites, referral analytics and a paper-ballot advocacy centre."],
            ["After the vote", "Post-election verification reports (\u201cMinimum Independently Witnessed Votes\u201d), evidence freezing with dual-custodian confirmation, and province-wide scaling."],
          ].map(([t, d]) => (
            <Card key={t}><CardContent className="p-5">
              <h3 className="font-heading font-semibold text-sm text-primary mb-1">{t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{d}</p>
            </CardContent></Card>
          ))}
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed mt-5">
          Before public launch, the platform will be reviewed by an Ontario municipal-election lawyer, a privacy
          professional, a cybersecurity professional, and an authorized commissioner/notary for any
          commissioned-declaration process.
        </p>
      </div>

      <Card className="mt-10 bg-secondary max-w-3xl">
        <CardContent className="p-6 sm:p-8">
          <div className="space-y-2 text-xs text-muted-foreground leading-relaxed">
            <p>{copy.disclaimers?.independent || COPY.disclaimers.independent}</p>
            <p>{copy.disclaimers?.not_replacement || COPY.disclaimers.notReplacement}</p>
            <p>{copy.disclaimers?.voluntary || COPY.disclaimers.voluntary}</p>
            <p>{copy.disclaimers?.governance || COPY.disclaimers.governance}</p>
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            <Button asChild><Link to="/how-it-works">How it works</Link></Button>
            <Button asChild variant="outline"><Link to="/contact">Contact us</Link></Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
